import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Form,
  Input,
  message,
  Spin,
  Result,
  Typography,
  Divider,
  Space,
  Image,
  Row,
  Col,
  Tag
} from 'antd';
import {
  LockOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  GiftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { addCanvasToPdf } from '../utils/canvasPdf';

const { Title, Text, Paragraph } = Typography;

const AddOnNotice = () => {
  const { addOnNoticeLink } = useParams();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [basicInfo, setBasicInfo] = useState(null);
  const [noticeData, setNoticeData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const contentRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchBasicInfo();
  }, [addOnNoticeLink]);

  const fetchBasicInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/addon-notices/link/${addOnNoticeLink}`);
      if (response.data.success) {
        setBasicInfo(response.data.data);
      }
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (values) => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API_BASE}/addon-notices/link/${addOnNoticeLink}/verify`,
        { passportLast4: values.passportLast4 }
      );
      if (response.data.success) {
        setNoticeData(response.data.data);
        setVerified(true);
      }
    } catch (error) {
      message.error(error.response?.data?.message || '验证失败');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;

    setDownloading(true);
    try {
      const content = contentRef.current;

      const canvas = await html2canvas(content, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      addCanvasToPdf(pdf, canvas, { margin: 5 });

      const fileName = `Add-on通知书_${noticeData?.addOnProduct?.name || 'addon'}_${noticeData?.tourist?.name || 'tourist'}.pdf`;
      pdf.save(fileName);
      message.success(`已开始下载：${fileName}`);
    } catch (error) {
      message.error('PDF生成失败');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getAttachmentUrl = (attachment) => {
    if (attachment.signedUrl) return attachment.signedUrl;
    if (attachment.filePath?.startsWith('/uploads/')) {
      return `${API_BASE}${attachment.filePath}`;
    }
    return attachment.filePath;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Result
          status="404"
          title="通知书不存在"
          subTitle="请检查链接是否正确，或联系您的旅行社"
        />
      </div>
    );
  }

  if (!verified) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #9254de 0%, #722ed1 100%)'
        }}
      >
        <Card style={{ width: 400, textAlign: 'center' }}>
          <div style={{ marginBottom: 24 }}>
            <GiftOutlined style={{ fontSize: 48, color: '#722ed1' }} />
          </div>
          <Title level={4}>Add-on 项目通知书</Title>
          <Text type="secondary">
            您好，{basicInfo?.touristName}！
            <br />
            请输入护照号后4位以查看您的 Add-on 项目通知书
          </Text>
          <Divider />
          <div style={{ marginBottom: 16 }}>
            <Tag color="purple" style={{ fontSize: 14, padding: '4px 12px' }}>
              {basicInfo?.addOnName}
            </Tag>
          </div>
          <Form onFinish={handleVerify} layout="vertical">
            <Form.Item
              name="passportLast4"
              rules={[
                { required: true, message: '请输入护照号后4位' },
                { len: 4, message: '请输入4位字符' }
              ]}
            >
              <Input
                size="large"
                placeholder="护照号后4位"
                maxLength={4}
                style={{ textAlign: 'center', letterSpacing: 8 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" block loading={verifying}
                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
              >
                验证并查看
              </Button>
            </Form.Item>
          </Form>
          <Text type="secondary" style={{ fontSize: 12 }}>
            产品：{basicInfo?.productName}
          </Text>
        </Card>
      </div>
    );
  }

  // 已验证，显示完整通知书
  const { tourist, tour, addOnProduct, touristAddOn, attachments } = noticeData;

  return (
    <div style={{ background: '#f0f2f5', minHeight: '100vh', padding: '24px 0' }}>
      {/* 工具栏 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#722ed1',
          padding: '12px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        className="no-print"
      >
        <Space>
          <CheckCircleOutlined style={{ color: '#fff' }} />
          <Text style={{ color: '#fff' }}>您好，{tourist.name}！这是您的 Add-on 项目通知书</Text>
        </Space>
        <Space>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            打印
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
          >
            下载PDF
          </Button>
        </Space>
      </div>

      {/* 通知书内容 */}
      <div
        ref={contentRef}
        style={{
          maxWidth: 800,
          margin: '60px auto 0',
          background: '#fff',
          padding: 40,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        {/* 标题 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ color: '#722ed1', marginBottom: 8 }}>
            Add-on 自费项目通知书
          </Title>
          <Text type="secondary">Optional Add-on Activity Notice</Text>
        </div>

        {/* 基本信息 */}
        <Card size="small" style={{ marginBottom: 24, background: '#f9f0ff' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Text strong>游客姓名：</Text>
              <Text>{tourist.passportName || tourist.name}</Text>
            </Col>
            <Col span={8}>
              <Text strong>产品名称：</Text>
              <Text>{tour?.productName}</Text>
            </Col>
            <Col span={8}>
              <Text strong>出发日期：</Text>
              <Text>{tour?.departureDate ? new Date(tour.departureDate).toLocaleDateString('zh-CN') : '-'}</Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}>
              <Text strong>销售顾问：</Text>
              <Text>{tourist.salesName}</Text>
            </Col>
            {touristAddOn.quantity > 1 && (
              <Col span={8}>
                <Text strong>预订数量：</Text>
                <Text>{touristAddOn.quantity}</Text>
              </Col>
            )}
          </Row>
        </Card>

        <Divider />

        {/* Add-on 项目详情 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <GiftOutlined style={{ fontSize: 24, color: '#722ed1', marginRight: 12 }} />
            <Title level={3} style={{ margin: 0, color: '#722ed1' }}>
              {addOnProduct.name}
            </Title>
          </div>

          {addOnProduct.destinationName && (
            <Tag color="purple" style={{ marginBottom: 16 }}>
              {addOnProduct.destinationName}
            </Tag>
          )}

          {/* 集合信息 */}
          <Card size="small" style={{ marginBottom: 16, borderColor: '#d3adf7' }}>
            <Row gutter={16}>
              <Col span={8}>
                <Space>
                  <Tag color="orange">Day {addOnProduct.dayNumber}</Tag>
                  <Text strong>行程日期</Text>
                </Space>
              </Col>
              {addOnProduct.meetingTime && (
                <Col span={8}>
                  <Space>
                    <ClockCircleOutlined style={{ color: '#722ed1' }} />
                    <Text strong>集合时间：</Text>
                    <Text>{addOnProduct.meetingTime}</Text>
                  </Space>
                </Col>
              )}
              {addOnProduct.duration && (
                <Col span={8}>
                  <Space>
                    <ClockCircleOutlined style={{ color: '#722ed1' }} />
                    <Text strong>活动时长：</Text>
                    <Text>{addOnProduct.duration}</Text>
                  </Space>
                </Col>
              )}
            </Row>
            {addOnProduct.meetingPoint && (
              <Row style={{ marginTop: 12 }}>
                <Col span={24}>
                  <Space>
                    <EnvironmentOutlined style={{ color: '#722ed1' }} />
                    <Text strong>集合地点：</Text>
                    <Text>{addOnProduct.meetingPoint}</Text>
                  </Space>
                </Col>
              </Row>
            )}
          </Card>

          {/* 项目简介 */}
          {addOnProduct.summary && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>项目简介</Title>
              <Paragraph style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 16, borderRadius: 4 }}>
                {addOnProduct.summary}
              </Paragraph>
            </div>
          )}

          {/* 详细说明 */}
          {addOnProduct.description && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>详细说明</Title>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                {addOnProduct.description}
              </Paragraph>
            </div>
          )}

          {/* 包含项目 */}
          {addOnProduct.includedItems && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ color: '#52c41a' }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                费用包含
              </Title>
              <Paragraph style={{ whiteSpace: 'pre-wrap', paddingLeft: 24 }}>
                {addOnProduct.includedItems}
              </Paragraph>
            </div>
          )}

          {/* 不包含项目 */}
          {addOnProduct.excludedItems && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ color: '#ff4d4f' }}>
                费用不包含
              </Title>
              <Paragraph style={{ whiteSpace: 'pre-wrap', paddingLeft: 24 }}>
                {addOnProduct.excludedItems}
              </Paragraph>
            </div>
          )}

          {/* 注意事项 */}
          {addOnProduct.notes && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ color: '#faad14' }}>
                注意事项
              </Title>
              <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                  {addOnProduct.notes}
                </Paragraph>
              </Card>
            </div>
          )}

          {/* 游客备注 */}
          {touristAddOn.notes && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>特殊备注</Title>
              <Paragraph type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                {touristAddOn.notes}
              </Paragraph>
            </div>
          )}
        </div>

        <Divider />

        {/* 附件图片 */}
        {attachments?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>附件 / Attachments</Title>
            <Row gutter={[16, 16]}>
              {attachments.map((att) => (
                <Col key={att.id} span={24}>
                  <Card size="small" title={att.name}>
                    {att.fileType === 'image' && (
                      <Image
                        src={getAttachmentUrl(att)}
                        alt={att.name}
                        style={{ maxWidth: '100%' }}
                      />
                    )}
                    {att.description && (
                      <Paragraph type="secondary" style={{ marginTop: 8 }}>
                        {att.description}
                      </Paragraph>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* 页脚 */}
        <div style={{ textAlign: 'center', marginTop: 40, paddingTop: 24, borderTop: '1px solid #eee' }}>
          <Text strong>祝您旅途愉快！/ Have a pleasant journey!</Text>
          <br />
          <Text type="secondary">
            万国旅行社 / Worldway Travel
            <br />
            地址：14C/155 Queen Street Auckland Central, Auckland, New Zealand
            <br />
            电话：0800 116 688 | 邮箱：confirmation@worldway.co.nz
          </Text>
        </div>
      </div>

      {/* 打印样式 */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AddOnNotice;
